import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DollarSign,
  TrendingUp,
  TrendingDown,
  Users,
  Megaphone,
  Briefcase,
  ArrowRight,
  Plus,
  Receipt,
  AlertCircle
} from 'lucide-react';
import { useLumisial } from '../context/LumisialContext';
import { useAuth } from '../context/AuthContext';
import { formatDate } from '../utils/formatters';
import { puedeOperarDineroDeLumisial } from '../utils/permissions';
import { movimientoService } from '../services/movimientoService';
import { miembroService } from '../services/miembroService';
import { campanaService } from '../services/campanaService';
import { actividadService } from '../services/actividadService';
import { Movimiento } from '../lib/supabase';
import { AporteModal } from '../components/AporteModal';
import { GastoModal } from '../components/GastoModal';
import { SelectorActividadRapidoModal } from '../components/SelectorActividadRapidoModal';
import { VentaActividadModal } from '../components/VentaActividadModal';
import { CostoActividadModal } from '../components/CostoActividadModal';
import { MovimientoCampanaModal } from '../components/MovimientoCampanaModal';
import { CATEGORIAS_EGRESO } from '../config/gastos';
import { Miembro } from '../lib/supabase';

interface Statistics {
  totalMiembros: number;
  campanasActivas: number;
  actividadesActivas: number;
  ingresos: number;
  egresos: number;
  /** Entradas por financiamiento de deudas: fuera de los ingresos, dentro del balance. */
  financiamiento: number;
  balance: number;
  cuotaIglesiaRecaudada: number;
  cuotaIglesiaJal: number;
  cuotaIglesiaPA: number;
  reembolsosPendientes: number;
  cantidadReembolsosPendientes: number;
  cantidadIngresos: number;
  cantidadEgresos: number;
  cantidadCuotaIglesia: number;
}

export const HomePage: React.FC = () => {
  const navigate = useNavigate();
  const { selectedLumisial } = useLumisial();
  const { usuario } = useAuth();
  // Registrar, editar y anular dinero de este Lumisial: sus juntas locales y la excepción técnica global.
  const puedeOperar = puedeOperarDineroDeLumisial(usuario, selectedLumisial);
  const [statistics, setStatistics] = useState<Statistics>({
    totalMiembros: 0,
    campanasActivas: 0,
    actividadesActivas: 0,
    ingresos: 0,
    egresos: 0,
    financiamiento: 0,
    balance: 0,
    cuotaIglesiaRecaudada: 0,
    cuotaIglesiaJal: 0,
    cuotaIglesiaPA: 0,
    reembolsosPendientes: 0,
    cantidadReembolsosPendientes: 0,
    cantidadIngresos: 0,
    cantidadEgresos: 0,
    cantidadCuotaIglesia: 0
  });
  const [recentMovimientos, setRecentMovimientos] = useState<Movimiento[]>([]);
  const [miembrosMap, setMiembrosMap] = useState<Record<string, Miembro>>({});
  const [loading, setLoading] = useState(true);
  const [isAporteModalOpen, setIsAporteModalOpen] = useState(false);
  const [isGastoModalOpen, setIsGastoModalOpen] = useState(false);
  const [showSelectorActividadModal, setShowSelectorActividadModal] = useState(false);
  const [showPedidoActividadModal, setShowPedidoActividadModal] = useState(false);
  const [showGastoActividadModal, setShowGastoActividadModal] = useState(false);
  const [actividadIdSeleccionada, setActividadIdSeleccionada] = useState('');
  const [showCampanaModal, setShowCampanaModal] = useState(false);

  useEffect(() => {
    if (selectedLumisial) {
      loadStatistics();
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedLumisial]);

  const loadStatistics = async () => {
    if (!selectedLumisial) return;

    try {
      setLoading(true);

      const [statsAndRecent, miembrosCount, campanasCount, actividadesCount, gastosPendientesReembolso] = await Promise.all([
        movimientoService.getStatsAndRecent(selectedLumisial.id, 5),
        miembroService.getCountByLumisial(selectedLumisial.id),
        campanaService.getActiveCountByLumisial(selectedLumisial.id),
        actividadService.getActiveCountByLumisial(selectedLumisial.id),
        movimientoService.getGastosPendientesReembolso(selectedLumisial.id)
      ]);

      const reembolsosPendientes = gastosPendientesReembolso.reduce((sum, gasto) => sum + Number(gasto.monto), 0);
      const cantidadReembolsosPendientes = gastosPendientesReembolso.length;

      setStatistics({
        totalMiembros: miembrosCount,
        campanasActivas: campanasCount,
        actividadesActivas: actividadesCount,
        ingresos: statsAndRecent.balance.ingresos,
        egresos: statsAndRecent.balance.egresos,
        financiamiento: statsAndRecent.balance.financiamiento,
        balance: statsAndRecent.balance.balance,
        cuotaIglesiaRecaudada: statsAndRecent.balance.cuotaIglesiaRecaudada,
        cuotaIglesiaJal: statsAndRecent.balance.cuotaIglesiaJal,
        cuotaIglesiaPA: statsAndRecent.balance.cuotaIglesiaPA,
        reembolsosPendientes,
        cantidadReembolsosPendientes,
        cantidadIngresos: statsAndRecent.cantidadIngresos,
        cantidadEgresos: statsAndRecent.cantidadEgresos,
        cantidadCuotaIglesia: statsAndRecent.cantidadCuotaIglesia
      });

      setRecentMovimientos(statsAndRecent.recentMovimientos);

      const miembrosIds = statsAndRecent.recentMovimientos
        .filter(m => m.origen_tipo === 'miembro' && m.origen_id)
        .map(m => m.origen_id as string);

      if (miembrosIds.length > 0) {
        const miembros = await miembroService.getByIds(miembrosIds);
        const miembrosMapTemp: Record<string, Miembro> = {};
        miembros.forEach((m: Miembro) => {
          miembrosMapTemp[m.id] = m;
        });
        setMiembrosMap(miembrosMapTemp);
      }
    } catch (error) {
      console.error('Error loading statistics:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP'
    }).format(amount);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-700"></div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-3xl font-bold text-slate-800 mb-2">
          Gestion Lumisial  {selectedLumisial?.nombre}
        </h2>
        <p className="text-slate-600">
          Panel de control y accesos rapidos
        </p>
      </div>

      {puedeOperar && (
      <div>
        <h3 className="text-lg font-semibold text-slate-700 mb-3">Registros rapidos</h3>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <button
            type="button"
            onClick={() => setIsAporteModalOpen(true)}
            className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 text-left group"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="bg-white/20 p-3 rounded-lg">
                <Plus className="w-6 h-6" />
              </div>
              <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <h3 className="text-xl font-bold mb-1">Registrar Aporte</h3>
            <p className="text-emerald-100 text-sm">Ingreso de miembro o externo</p>
          </button>

          <button
            type="button"
            onClick={() => setIsGastoModalOpen(true)}
            className="bg-gradient-to-br from-orange-500 to-orange-600 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 text-left group"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="bg-white/20 p-3 rounded-lg">
                <Receipt className="w-6 h-6" />
              </div>
              <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <h3 className="text-xl font-bold mb-1">Registrar Gasto</h3>
            <p className="text-orange-100 text-sm">Gasto general o litúrgico</p>
          </button>

          <button
            type="button"
            onClick={() => setShowSelectorActividadModal(true)}
            className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 text-left group"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="bg-white/20 p-3 rounded-lg">
                <Briefcase className="w-6 h-6" />
              </div>
              <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <h3 className="text-xl font-bold mb-1">Actividad</h3>
            <p className="text-blue-100 text-sm">Pedido o Gasto</p>
          </button>

          <button
            type="button"
            onClick={() => setShowCampanaModal(true)}
            className="bg-gradient-to-br from-teal-500 to-teal-600 text-white rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-200 hover:scale-105 text-left group"
          >
            <div className="flex items-center justify-between mb-2">
              <div className="bg-white/20 p-3 rounded-lg">
                <Megaphone className="w-6 h-6" />
              </div>
              <ArrowRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity" />
            </div>
            <h3 className="text-xl font-bold mb-1">Mov. Campaña</h3>
            <p className="text-teal-100 text-sm">Registrar movimiento</p>
          </button>
        </div>
      </div>
      )}


      <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
          <div className="space-y-3">
            <div className="flex items-center gap-2">
              <div className="bg-emerald-100 p-2 rounded-lg">
                <TrendingUp className="w-4 h-4 text-emerald-600" />
              </div>
              <span className="text-xs font-medium text-emerald-600">Ingresos</span>
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-800">{formatCurrency(statistics.ingresos)}</p>
              <p className="text-xs text-slate-500">{statistics.cantidadIngresos} {statistics.cantidadIngresos === 1 ? 'movimiento' : 'movimientos'}</p>
            </div>

            <div className="border-t border-slate-200 pt-3">
              <div className="flex items-center gap-2 mb-2">
                <div className="bg-red-100 p-2 rounded-lg">
                  <TrendingDown className="w-4 h-4 text-red-600" />
                </div>
                <span className="text-xs font-medium text-red-600">Egresos</span>
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{formatCurrency(statistics.egresos)}</p>
                <p className="text-xs text-slate-500">{statistics.cantidadEgresos} {statistics.cantidadEgresos === 1 ? 'movimiento' : 'movimientos'}</p>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-blue-100 p-3 rounded-lg">
              <DollarSign className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-sm font-medium text-blue-600">Balance</span>
          </div>
          <p className={`text-3xl font-bold ${statistics.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
            {formatCurrency(statistics.balance)}
          </p>
          {statistics.financiamiento > 0 && (
            <p className="text-xs text-slate-500 mt-1">
              Incluye {formatCurrency(statistics.financiamiento)} de financiamiento recibido
            </p>
          )}
        </div>

        {/* Medida puente: enlace directo desde la cifra al lugar donde se atiende. Cuando exista la
            bandeja de tareas pendientes (.planning/seeds/bandeja-de-tareas-pendientes.md), que va a
            concentrar el trabajo accionable en un ícono junto a la campana, este enlace se evalúa de nuevo. */}
        <div className="relative bg-white rounded-xl shadow-md p-6 border border-slate-200">
          {statistics.reembolsosPendientes > 0 && (
            <button
              type="button"
              onClick={() => { navigate('/lumisial/balance/movimientos'); }}
              className="absolute inset-0 rounded-xl cursor-pointer"
              aria-label="Ver los gastos pendientes de reembolso"
            />
          )}
          <div className="relative pointer-events-none">
            <div className="flex items-center justify-between mb-4">
              <div className={`p-3 rounded-lg ${statistics.reembolsosPendientes > 0 ? 'bg-amber-100' : 'bg-slate-100'}`}>
                <AlertCircle className={`w-6 h-6 ${statistics.reembolsosPendientes > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
              </div>
              <span className={`text-sm font-medium ${statistics.reembolsosPendientes > 0 ? 'text-amber-600' : 'text-slate-500'}`}>Reembolsos</span>
            </div>
            <p className={`text-3xl font-bold ${statistics.reembolsosPendientes > 0 ? 'text-amber-600' : 'text-slate-400'}`}>
              {formatCurrency(statistics.reembolsosPendientes)}
            </p>
            <p className="text-xs text-slate-500 mt-2">
              {statistics.cantidadReembolsosPendientes} {statistics.cantidadReembolsosPendientes === 1 ? 'gasto pendiente' : 'gastos pendientes'}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
          <div className="flex items-center justify-between mb-4">
            <div className="bg-amber-100 p-3 rounded-lg">
              <DollarSign className="w-6 h-6 text-amber-600" />
            </div>
            <span className="text-sm font-medium text-amber-600">Cuota Iglesia</span>
          </div>
          <p className="text-3xl font-bold text-slate-800">{formatCurrency(statistics.cuotaIglesiaRecaudada)}</p>
          <div className="mt-2 space-y-1">
            {statistics.cuotaIglesiaJal > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">JAL</span>
                <span className="text-xs text-slate-600 font-medium">{formatCurrency(statistics.cuotaIglesiaJal)}</span>
              </div>
            )}
            {statistics.cuotaIglesiaPA > 0 && (
              <div className="flex justify-between items-center">
                <span className="text-xs px-1.5 py-0.5 rounded bg-slate-200 text-slate-600 font-medium">PA</span>
                <span className="text-xs text-slate-600 font-medium">{formatCurrency(statistics.cuotaIglesiaPA)}</span>
              </div>
            )}
            <p className="text-xs text-slate-500">
              {statistics.cantidadCuotaIglesia} {statistics.cantidadCuotaIglesia === 1 ? 'aporte' : 'aportes'}
            </p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-3 gap-6">
        <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <Users className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-700">Total Miembros</h3>
          </div>
          <p className="text-4xl font-bold text-slate-800">{statistics.totalMiembros}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <Megaphone className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-700">Campanas Activas</h3>
          </div>
          <p className="text-4xl font-bold text-slate-800">{statistics.campanasActivas}</p>
        </div>

        <div className="bg-white rounded-xl shadow-md p-6 border border-slate-200">
          <div className="flex items-center gap-3 mb-2">
            <Briefcase className="w-5 h-5 text-slate-600" />
            <h3 className="font-semibold text-slate-700">Actividades Activas</h3>
          </div>
          <p className="text-4xl font-bold text-slate-800">{statistics.actividadesActivas}</p>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-md border border-slate-200">
        <div className="p-6 border-b border-slate-200">
          <h3 className="text-xl font-bold text-slate-800">Movimientos Recientes</h3>
        </div>
        <div className="divide-y divide-slate-200">
          {recentMovimientos.length === 0 ? (
            <div className="p-6 text-center text-slate-500">
              No hay movimientos registrados
            </div>
          ) : (
            recentMovimientos.map((mov) => {
              const miembroReembolso = mov.reembolso_a_miembro_id ? miembrosMap[mov.reembolso_a_miembro_id] : null;

              return (
                <div key={mov.id} className="p-4 hover:bg-slate-50 transition-colors">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3">
                        <div
                          className={`w-2 h-2 rounded-full ${
                            mov.tipo === 'ingreso' ? 'bg-emerald-500' : 'bg-red-500'
                          }`}
                        ></div>
                        <span className="font-medium text-slate-800">
                          {mov.descripcion || 'Sin descripcion'}
                        </span>
                      </div>
                      <div className="flex items-center gap-2 flex-wrap mt-1 ml-5">
                        <span className="text-sm text-slate-500">{formatDate(mov.fecha)}</span>
                        {mov.origen_tipo && (
                          <span className="text-xs bg-slate-100 text-slate-600 px-2 py-1 rounded">
                            {mov.origen_tipo}
                          </span>
                        )}
                        {mov.tipo === 'egreso' && mov.categoria_egreso && (() => {
                          let egresoClass = 'bg-purple-100 text-purple-700';
                          if (mov.categoria_egreso === 'general') egresoClass = 'bg-blue-100 text-blue-700';
                          else if (mov.categoria_egreso === 'misional') egresoClass = 'bg-green-100 text-green-700';
                          return (
                            <span className={`text-xs px-2 py-1 rounded font-medium ${egresoClass}`}>
                              {CATEGORIAS_EGRESO[mov.categoria_egreso]}
                            </span>
                          );
                        })()}
                        {mov.tipo === 'egreso' && mov.subcategoria_egreso && (
                          <span className="text-xs bg-slate-200 text-slate-700 px-2 py-1 rounded">
                            {mov.subcategoria_egreso}
                          </span>
                        )}
                        {mov.requiere_reembolso && !mov.reembolsado && (
                          <span className="text-xs bg-amber-100 text-amber-700 px-2 py-1 rounded font-medium flex items-center gap-1">
                            <AlertCircle className="w-3 h-3" />
                            Pendiente Reembolso
                          </span>
                        )}
                        {mov.reembolsado && (
                          <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded font-medium">
                            Reembolsado
                          </span>
                        )}
                      </div>
                      {mov.requiere_reembolso && !mov.reembolsado && (
                        <div className="text-xs text-slate-600 mt-1 ml-5">
                          Reembolsar a: {' '}
                          {miembroReembolso
                            ? `${miembroReembolso.nombres} ${miembroReembolso.apellidos}`
                            : mov.reembolso_a_nombre_externo || 'No especificado'
                          }
                        </div>
                      )}
                    </div>
                    <div
                      className={`text-lg font-bold ${
                        mov.tipo === 'ingreso' ? 'text-emerald-600' : 'text-red-600'
                      }`}
                    >
                      {mov.tipo === 'ingreso' ? '+' : '-'} {formatCurrency(Number(mov.monto))}
                    </div>
                  </div>
                </div>
              );
            })
          )}
        </div>
      </div>

      <AporteModal
        isOpen={isAporteModalOpen}
        onClose={() => setIsAporteModalOpen(false)}
        onSuccess={loadStatistics}
      />

      <GastoModal
        isOpen={isGastoModalOpen}
        onClose={() => setIsGastoModalOpen(false)}
        onSuccess={loadStatistics}
      />

      <SelectorActividadRapidoModal
        isOpen={showSelectorActividadModal}
        onClose={() => setShowSelectorActividadModal(false)}
        onSelectPedido={(id) => {
          setActividadIdSeleccionada(id);
          setShowPedidoActividadModal(true);
        }}
        onSelectGasto={(id) => {
          setActividadIdSeleccionada(id);
          setShowGastoActividadModal(true);
        }}
      />

      {showPedidoActividadModal && actividadIdSeleccionada && (
        <VentaActividadModal
          isOpen={showPedidoActividadModal}
          onClose={() => {
            setShowPedidoActividadModal(false);
            setActividadIdSeleccionada('');
          }}
          onSuccess={loadStatistics}
          actividadId={actividadIdSeleccionada}
          miembros={[]}
        />
      )}

      {showGastoActividadModal && actividadIdSeleccionada && (
        <CostoActividadModal
          isOpen={showGastoActividadModal}
          onClose={() => {
            setShowGastoActividadModal(false);
            setActividadIdSeleccionada('');
          }}
          onSuccess={loadStatistics}
          actividadId={actividadIdSeleccionada}
        />
      )}

      <MovimientoCampanaModal
        isOpen={showCampanaModal}
        onClose={() => setShowCampanaModal(false)}
        onSuccess={loadStatistics}
      />

    </div>
  );
};
